import { useEffect, useRef, useState } from "react";
import { Upload, FileText, Image as ImageIcon, Trash2, Download, FileSpreadsheet, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import FilePreview from "@/components/FilePreview";

const ICON_FOR = (ct) => {
  if (!ct) return FileText;
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct.includes("spreadsheet") || ct.includes("excel") || ct.includes("csv")) return FileSpreadsheet;
  return FileText;
};

function fmtSize(b) {
  if (!b) return "";
  if (b > 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  if (b > 1024) return `${(b / 1024).toFixed(0)} kB`;
  return `${b} B`;
}

export default function FileManager({ entityType, entityId }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  const load = () => {
    api.get("/files", { params: { entity_type: entityType, entity_id: entityId } })
      .then((r) => setFiles(r.data));
  };

  useEffect(() => {
    if (entityId) load();
    // eslint-disable-next-line
  }, [entityId, entityType]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Max 10 MB");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/files/upload", fd, {
        params: { entity_type: entityType, entity_id: entityId },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (fid) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await api.delete(`/files/${fid}`);
      load();
      toast.success("File deleted");
    } catch (err) {
      toast.error("Cannot delete");
    }
  };

  const downloadUrl = (fid) => {
    const token = localStorage.getItem("sodimate_token");
    return `${API}/files/${fid}/download?auth=${encodeURIComponent(token)}`;
  };

  return (
    <div data-testid="file-manager">
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow">Files · {files.length}</div>
        <label className="btn-ghost text-xs cursor-pointer" data-testid="upload-file-button">
          <Upload size={12} /> {busy ? "Uploading…" : "Upload"}
          <input ref={inputRef} type="file" className="hidden" onChange={upload} disabled={busy}
            accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv,.txt,.docx" />
        </label>
      </div>

      {files.length === 0 ? (
        <div className="text-center text-gray-400 text-sm py-8">
          No files. Drop technical specs, plant photos or contracts here.
        </div>
      ) : (
        <div className="space-y-2">
          {files.map((f) => {
            const Icon = ICON_FOR(f.content_type);
            const previewable = (f.content_type || "").startsWith("image/")
              || (f.content_type || "").includes("pdf");
            return (
              <div key={f.id} className="flex items-center gap-3 p-3 border border-gray-200 hover:border-[#004B87] transition-colors"
                data-testid={`file-${f.id}`}>
                <button
                  onClick={() => previewable && setPreview(f)}
                  disabled={!previewable}
                  className={`w-9 h-9 grid place-items-center bg-[#004B87]/5 border border-[#004B87]/20 ${previewable ? "hover:bg-[#004B87]/10 cursor-pointer" : ""}`}
                >
                  <Icon size={14} className="text-[#004B87]" />
                </button>
                <button
                  onClick={() => previewable && setPreview(f)}
                  disabled={!previewable}
                  className={`flex-1 min-w-0 text-left ${previewable ? "cursor-pointer" : ""}`}
                  data-testid={`preview-file-${f.id}`}
                >
                  <div className="text-sm font-semibold truncate">{f.filename}</div>
                  <div className="text-xs text-gray-400">
                    {fmtSize(f.size)} · {new Date(f.created_at).toLocaleDateString()}
                    {f.user_name && ` · ${f.user_name}`}
                  </div>
                </button>
                {previewable && (
                  <button onClick={() => setPreview(f)}
                    className="text-gray-400 hover:text-[#004B87] p-1" title="Preview">
                    <Eye size={14} />
                  </button>
                )}
                <a href={downloadUrl(f.id)} target="_blank" rel="noreferrer" download={f.filename}
                  className="text-gray-400 hover:text-[#004B87] p-1" title="Download">
                  <Download size={14} />
                </a>
                <button onClick={() => remove(f.id)}
                  className="text-gray-400 hover:text-red-600 p-1" title="Delete">
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <FilePreview file={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
